import React, { useState } from 'react';
import { Pane, Text, Button, TextInput, Select, toaster, Heading } from 'evergreen-ui';
import { useMutation, useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import QUERY_GLOBAL_DATA from '../../../graphql/queries/QueryGlobalData';

const CREATE_CREATOR = gql`
  mutation CreateCreator(
    $publisherId: Int!,
    $displayName: String!,
    $email: String!,
    $paymentType: Int!,
    $paymentName: String!,
    $photoUrl: String,
    $biography: String,
    $phone: String,
    $website: String,
    $line1: String,
    $line2: String,
    $city: String,
    $state: String,
    $zipcode: String,
    $country: String
  ) {
    createCreator(
      publisherId: $publisherId,
      displayName: $displayName,
      email: $email,
      paymentType: $paymentType,
      paymentName: $paymentName,
      photoUrl: $photoUrl,
      biography: $biography,
      phone: $phone,
      website: $website,
      line1: $line1,
      line2: $line2,
      city: $city,
      state: $state,
      zipcode: $zipcode,
      country: $country
    ) {
      id
      displayName
    }
  }
`;

const CREATE_PRODUCT = gql`
  mutation CreateProduct(
    $publisherId: ID!,
    $title: String!,
    $formatId: ID!,
    $isbn: String!,
    $retailPrice: Float!,
    $creatorIds: String,
    $status: String!
  ) {
    createProduct(
      publisherId: $publisherId,
      title: $title,
      formatId: $formatId,
      isbn: $isbn,
      retailPrice: $retailPrice,
      creatorIds: $creatorIds,
      status: $status
    ) {
      product {
        id
        isbn
        retailPrice
        format {
          description
        }
        productWork {
          title
        }
      }
      message
    }
  }
`;

const CREATE_CUSTOMER = gql`
  mutation CreateCustomer($publisherId: Int!, $customer: String!) {
    createCustomer(publisherId: $publisherId, customer: $customer) {
      message
    }
  }
`;

const CREATE_ORDER = gql`
  mutation CreateOrder($publisherId: Int!, $order: String!) {
    createOrder(publisherId: $publisherId, order: $order) {
      message
    }
  }
`;

const CREATE_ORDER_PRODUCT = gql`
  mutation CreateOrderProduct($productId: Int!, $orderId: Int!, $orderProduct: String!) {
    createOrderProduct(productId: $productId, orderId: $orderId, orderProduct: $orderProduct) {
      message
    }
  }
`;

const UPDATE_ONBOARDING_STATUS = gql`
  mutation UpdateOnboardingStatus($publisherId: ID!) {
    updateOnboardingStatus(publisherId: $publisherId)
  }
`;

const OnboardingOverlay = ({ onDismiss, publisherId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    creator: 'Octavia Butler',
    title: 'Parable of the Sower',
    isbn: '123456789',
    retailPrice: '16.99',
    customerName: "Powell's Books",
    quantity: '10'
  });
  const [createdData, setCreatedData] = useState({
    creatorId: null,
    productId: null,
    customerId: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch payment types for this publisher
  const { data: globalData } = useQuery(QUERY_GLOBAL_DATA, {
    variables: {
      publisher: parseInt(publisherId),
      user: 1 // This is required by the query but not used for payment types
    },
    fetchPolicy: 'network-only'
  });

  const [createCreator] = useMutation(CREATE_CREATOR);
  const [createProduct] = useMutation(CREATE_PRODUCT);
  const [createCustomer] = useMutation(CREATE_CUSTOMER);
  const [createOrder] = useMutation(CREATE_ORDER);
  const [createOrderProduct] = useMutation(CREATE_ORDER_PRODUCT);
  const [updateOnboardingStatus] = useMutation(UPDATE_ONBOARDING_STATUS);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateCreator = async () => {
    try {
      // Get the first payment type from the publisher's payment types, or default to 1 if none found
      const paymentTypeId = globalData?.paymenttypes?.length > 0
        ? parseInt(globalData.paymenttypes[0].id)
        : 1;

      const { data } = await createCreator({
        variables: {
          publisherId: parseInt(publisherId),
          displayName: formData.creator,
          email: 'octavia-butler@example.com',
          paymentType: paymentTypeId,
          paymentName: formData.creator,
          photoUrl: "",
          biography: "",
          phone: "",
          website: "",
          line1: "",
          line2: "",
          city: "",
          state: "",
          zipcode: "",
          country: ""
        }
      });
      setCreatedData(prev => ({ ...prev, creatorId: data.createCreator.id }));
      setCurrentStep(2);
      toaster.success('Creator added successfully!');
    } catch (error) {
      toaster.danger('Error creating creator: ' + error.message);
    }
  };

  const handleCreateProduct = async () => {
    if (!formData.title.trim() || !formData.isbn.trim() || !formData.retailPrice) {
      toaster.danger('All product fields are required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Format creator data as required by the new mutation
      const creatorIdsString = createdData.creatorId ?
        JSON.stringify([{creatorId: parseInt(createdData.creatorId), onixCodeId: 1}]) :
        null;

      if (!globalData || !globalData.onixproductavailabilitys || globalData.onixproductavailabilitys.length === 0) {
        toaster.danger('Product status options not loaded. Please try again.');
        setError('Product status options not loaded.');
        setIsLoading(false);
        return;
      }

      let defaultStatusIdString;
      const activeStatus = globalData.onixproductavailabilitys.find(s => s.description === 'Active');

      if (activeStatus && activeStatus.id) {
        defaultStatusIdString = activeStatus.id;
      } else if (globalData.onixproductavailabilitys.length > 0 && globalData.onixproductavailabilitys[0].id) {
        defaultStatusIdString = globalData.onixproductavailabilitys[0].id;
        toaster.info("Could not find 'Active' status, using first available product status as default for onboarding.");
      }

      if (!defaultStatusIdString) {
          toaster.danger('Failed to determine a valid default product status ID string.');
          setError('Failed to determine a valid default product status ID string.');
          setIsLoading(false);
          return;
      }

      const { data } = await createProduct({
        variables: {
          publisherId: parseInt(publisherId),
          title: formData.title,
          formatId: 2, // Using format_id 2 as default from the old code
          isbn: formData.isbn,
          retailPrice: parseFloat(formData.retailPrice),
          creatorIds: creatorIdsString,
          status: defaultStatusIdString
        }
      });
      setCreatedData(prev => ({ ...prev, productId: data.createProduct.product.id }));
      setCurrentStep(3);
      toaster.success('Product added successfully!');
    } catch (error) {
      setError(error.message);
      toaster.danger('Error creating product: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!formData.customerName.trim()) {
      toaster.danger('Company name is required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const customerData = {
        company: formData.customerName,
        firstName: "",  // Adding these fields to ensure we have a valid customer
        lastName: "",
        email: "",
        phone: ""
      };

      const { data } = await createCustomer({
        variables: {
          publisherId: parseInt(publisherId),
          customer: JSON.stringify(customerData)
        }
      });

      const customerId = data.createCustomer.message;
      setCreatedData(prev => ({ ...prev, customerId }));
      setCurrentStep(4);
      toaster.success('Customer added successfully!');
    } catch (error) {
      setError(error.message);
      toaster.danger('Error creating customer: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      toaster.danger('Please enter a valid quantity');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // First create the order
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const orderData = {
        customer: { id: parseInt(createdData.customerId) },  // Ensure the ID is an integer
        orderStatus: "Ready",
        paid: true,
        paidDate: today
      };

      const orderResult = await createOrder({
        variables: {
          publisherId: parseInt(publisherId),
          order: JSON.stringify(orderData)
        }
      });

      const orderId = orderResult.data.createOrder.message;

      // Then create the order product
      const orderProductParams = {
        quantity: parseInt(formData.quantity),
        retailPrice: parseFloat(formData.retailPrice),
        calcDiscount: 0,
        calcPrice: parseFloat(formData.retailPrice)
      };

      await createOrderProduct({
        variables: {
          productId: parseInt(createdData.productId),
          orderId: parseInt(orderId),
          orderProduct: JSON.stringify(orderProductParams)
        }
      });

      // Fire confetti
      const duration = 700;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 10,
          angle: 90,
          spread: 100,
          origin: { x: 0.5, y: .8 }
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();

      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, duration));

      setCurrentStep(5);
      toaster.success('Order added successfully!');
    } catch (error) {
      setError(error.message);
      toaster.danger('Error creating order: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await updateOnboardingStatus({
        variables: {
          publisherId
        }
      });

      // Save to localStorage to prevent flash on refresh
      localStorage.setItem(`onboarding_${publisherId}`, 'completed');

      // Call the onDismiss prop
      onDismiss();

      // Dispatch a custom event to trigger dashboard data refresh
      window.dispatchEvent(new CustomEvent('refreshDashboardData'));
    } catch (error) {
      toaster.danger('Error updating onboarding status');
    }
  };

  const renderMiddleColumn = () => {
    switch (currentStep) {
      case 0:
        return (
          <Pane>
            <Heading size={900} marginBottom={16}>It's better with data.</Heading>
            <Text size={500}>Let us help you quickly enter a few things so you can see WorkingLit in action.</Text>
            <br />
            <br />
            <Button
              backgroundColor="var(--maroon)"
              color="white"
              intent="success"
              className="new-button"
              size="large"
              fontWeight="bold"
              onClick={() => setCurrentStep(1)}
              marginTop={16}
            >
              Start
            </Button>
          </Pane>
        );
      case 1:
        return (
          <Pane>
            <Text size={600} fontWeight="bold">Add Creator</Text>
            <br />
            <Text size={600}>Such as an author or artist or translator </Text>
            <br /><br />
            <Text size={400}>Creator name</Text><br />
            <TextInput
              value={formData.creator}
              onChange={e => handleInputChange('creator', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') handleCreateCreator();
              }}
              placeholder="Creator Name"
              size="large"
              fontWeight="bold"
              label="Creator Name"
              marginTop={8}
              isInvalid={error && !formData.creator.trim()}
            />
            <br />
            <Button
              backgroundColor="var(--maroon)"
              color="white"
              intent="success"
              className="new-button"
              size="large"
              onClick={handleCreateCreator}
              marginTop={16}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Add
            </Button>
          </Pane>
        );
      case 2:
        return (
          <Pane>
            <Text size={600}>Great! Now add the product title created by {formData.creator}</Text><br/><br/>
            <Text size={400}>Title</Text><br/>
            <TextInput
              value={formData.title}
              onChange={e => handleInputChange('title', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter' && formData.title && formData.isbn && formData.retailPrice) {
                  handleCreateProduct();
                }
              }}
              placeholder="Title"
              marginTop={8}
              isInvalid={error && !formData.title.trim()}
            />
            <br/>
            <Text size={400}>ISBN</Text><br/>
            <TextInput
              value={formData.isbn}
              onChange={e => handleInputChange('isbn', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter' && formData.title && formData.isbn && formData.retailPrice) {
                  handleCreateProduct();
                }
              }}
              placeholder="ISBN"
              marginTop={8}
              isInvalid={error && !formData.isbn.trim()}
            />
            <br/>
            <Text size={400}>Retail Price</Text><br/>
            <TextInput
              value={formData.retailPrice}
              onChange={e => handleInputChange('retailPrice', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter' && formData.title && formData.isbn && formData.retailPrice) {
                  handleCreateProduct();
                }
              }}
              placeholder="Retail Price"
              marginTop={8}
              isInvalid={error && !formData.retailPrice}
            />
            <br />
            <Button
              backgroundColor="var(--maroon)"
              color="white"
              className="new-button"

              intent="success"
              size="large"
              onClick={handleCreateProduct}
              marginTop={16}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Add
            </Button>
          </Pane>
        );
      case 3:
        return (
          <Pane>
            <Text size={600}>Excellent! Now let's add a customer. This can be a distributor or any shop that might order {formData.title}</Text>
            <TextInput
              value={formData.customerName}
              onChange={e => handleInputChange('customerName', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') handleCreateCustomer();
              }}
              placeholder="Company Name"
              marginTop={8}
            />
            <br />
            <Button
              backgroundColor="var(--maroon)"
              color="white"
              className="new-button"
              intent="success"
              size="large"
              onClick={handleCreateCustomer}
              marginTop={16}
            >
              Add
            </Button>
          </Pane>
        );
      case 4:
        return (
          <Pane>
            <Text size={600}>Excellent! Now let's add our first order.</Text><br/>
            <Text size={400}>Customer: {formData.customerName}</Text><br/>
            <Text size={400}>Product: {formData.title}</Text><br/>
            <Text size={400}>Enter quantity sold</Text><br/>
            <TextInput
              value={formData.quantity}
              onChange={e => handleInputChange('quantity', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') handleCreateOrder();
              }}
              placeholder="Quantity"
              marginTop={8}
            />  <br/>
            <Button
              backgroundColor="var(--maroon)"
              color="white"
              className="new-button"
              intent="success"
              size="large"
              onClick={handleCreateOrder}
              marginTop={16}
            >
              Add
            </Button>

            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            Don't worry, you can undo all this later.
          </Pane>
        );
      case 5:
        return (
          <Pane>
            <Text size={600}>Fantastic!<br/>You have some data to work with now.</Text><br/><br/>
            <Text size={400} marginTop={16}>
              A next step might be checking out how to set up <Link to="/royalties">Royalties</Link>,
              adding <Link to="/expenses">Expenses</Link>, or exploring our <Link to="/reporting">Reporting</Link> page.
            </Text><br/><br/>
            <Text size={400} marginTop={8}>
              Or go crazy by using our import tools to bring in your products, orders, and expenses by the thousands.
            </Text><br/><br/>
            <br />
            <Button
              backgroundColor="var(--maroon)"
              color="white"
              className="new-button"
              intent="success"
              size="large"
              onClick={handleDismiss}
              marginTop={16}
            >
              Get Started
            </Button>
          </Pane>
        );
      default:
        return null;
    }
  };

  return (
    <Pane
      position="fixed"
      top={16}
      left={0}
      right={16}
      bottom={16}
      display="flex"
      background="rgba(255, 255, 255, 0.9)"
      zIndex={100}
      marginLeft={190}
      borderRadius={8}
      border="1px solid #E6E8F0"
      elevation={2}
    >
      {/* Left Column */}
      <Pane width="33%" padding={32} borderRight="1px solid #E6E8F0">
        <Heading size={900} marginBottom={16}>Welcome to WorkingLit!</Heading>
        <Text size={600} marginBottom={16} lineHeight={2}>
          It's going to be great. <br /><br />All of your data will be in one place. Doing royalties will be a cinch.
          You will be able to track the profit & loss of any book. And you'll have far more time to do the work you love.
        </Text>
        <br />
        <br />
        <br />
        <Text size={500} fontWeight="bold" marginBottom={16}>Watch our getting started video:</Text>
        <Pane marginTop={16} position="relative" paddingBottom="58%" height={0}>
          <iframe
            src="https://www.loom.com/embed/e075bf1dd6e243438da129ab46c74f1f?sid=c33effea-829f-4ee0-9fa8-a306448309d2"
            frameBorder="0"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
        </Pane>
      </Pane>

      {/* Middle Column */}
      <Pane width="34%" padding={32} lineHeight={2} borderRight="1px solid #E6E8F0">
        {renderMiddleColumn()}
      </Pane>

      {/* Right Column */}
      <Pane width="33%" lineHeight={2} padding={32}>
        <Heading size={900} marginBottom={16}>Your dashboard awaits...</Heading>
        <Text size={500}>
          Beneath this quick-start screen is your dashboard. It will look a little empty until you have entered data.
          But if you're brave, you can dismiss this screen and dig right into the weeds.
        </Text>
        <Button
          backgroundColor="var(--maroon)"
          color="white"
          className="new-button"
          intent="success"
          size="large"
          fontWeight="bold"
          onClick={handleDismiss}
          marginTop={16}
        >
          Close Onboarding Quick Start
        </Button>
      </Pane>
    </Pane>
  );
};

export default OnboardingOverlay;
